import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { getSiteHostname } from '../utils/locationPermission';
import './LocationPermissionModal.css';

const ANIMATION_MS = 180;

const LocationPermissionModal = ({
  isOpen,
  onClose,
  onAllowAlways,
  onAllowOnce,
  onNeverAllow,
  loading = false,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);
  const siteName = getSiteHostname();

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => setIsVisible(true));
      return undefined;
    }

    setIsVisible(false);
    const timeout = setTimeout(() => setShouldRender(false), ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!shouldRender) return undefined;

    const handleEsc = (event) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [shouldRender, onClose, loading]);

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`location-permission-overlay ${isVisible ? 'open' : 'closing'}`}
      onClick={() => {
        if (!loading) onClose();
      }}
      role="presentation"
    >
      <div
        className={`location-permission-modal ${isVisible ? 'open' : 'closing'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-permission-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="location-permission-header">
          <h2 id="location-permission-title">
            {siteName} wants to
          </h2>
          <button
            type="button"
            className="location-permission-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="location-permission-body">
          <FaMapMarkerAlt className="location-permission-pin" aria-hidden="true" />
          <span>Know your location</span>
        </div>

        <div className="location-permission-actions">
          <button
            type="button"
            className="location-permission-btn"
            onClick={onAllowAlways}
            disabled={loading}
          >
            {loading ? 'Detecting location…' : 'Allow while visiting the site'}
          </button>
          <button
            type="button"
            className="location-permission-btn"
            onClick={onAllowOnce}
            disabled={loading}
          >
            Allow this time
          </button>
          <button
            type="button"
            className="location-permission-btn location-permission-btn--muted"
            onClick={onNeverAllow}
            disabled={loading}
          >
            Never allow
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LocationPermissionModal;
